import React from 'react'
import PropTypes from 'prop-types'
import { Avatar, Box, IconButton, ListItemIcon, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import ImageComponent from '@professocial/professocialFormComponents/FormComponents/ImageComponent'
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRounded from '@mui/icons-material/KeyboardArrowUpRounded'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import Logout from '@mui/icons-material/Logout'
import SettingsOutlined from '@mui/icons-material/SettingsOutlined'
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined'
import { Navigation } from '@professocial/professocialTypes/types/commonTypes'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'

import { useTranslation } from 'react-i18next'
import { useLocation, NavLink } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getUser } from '../../../store/common/common.selectors'

export const accountNavigationList: Navigation[] = [
  {
    value: 'settings',
    label: 'settings',
    key: 'settings',
    icon: <SettingsOutlined fontSize='medium' />,
  },
  {
    value: 'privacy',
    label: 'privacy',
    key: 'privacy',
    icon: <AdminPanelSettingsOutlined fontSize='medium' />,
  },
  {
    value: 'termsOfUse',
    label: 'termsOfUse',
    key: 'termsOfUse',
    icon: <FactCheckOutlinedIcon fontSize='medium' />,
  },
  {
    value: 'logout',
    label: 'logout',
    key: 'logout',
    icon: <Logout fontSize='medium' />,
  },
]

const useStyles = makeStyles((theme: Theme) => ({
  headerContainer: {
    position: 'sticky',
    padding: theme.spacing(2, 2.5),
    height: theme.spacing(10),
    width: '100%',
    top: 0,
    zIndex: 99,
    backgroundColor: theme.palette.common.white,
  },
  logo: {
    height: '100%',
    '& img': {
      maxHeight: '100%',
      width: '100%',
    },
  },
  userDropDown: {
    color: theme.palette.primary.main,
  },
  active: {
    '& .MuiMenuItem-root': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
  },
}))

interface IProps {
  user?: any
  logo?: string
  setNaviChange?: (naviChanged: boolean) => void
}

const Header: React.FC<IProps> = React.memo((props) => {
  const classes = useStyles()
  const user = useSelector(getUser)

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const theme = useTheme()
  const { t } = useTranslation()

  const location = useLocation()

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <Stack
      className={`headerContainer ${classes.headerContainer}`}
      direction='row'
      justifyContent='space-between'
      alignItems='center'
    >
      <Box className={classes.logo}>
        <ImageComponent
          src={props.logo}
          fallback={require('../../assets/images/defaultFacebookProfileImage.jpg')}
        />
      </Box>
      <Box className={`userDropDown ${classes.userDropDown}`}>
        <Stack direction='row' alignItems='center'>
          <Avatar sx={{ width: 54, height: 54, mr: 1.2, backgroundColor: 'transparent', color: theme.palette.primary.main }}>
            <AccountCircleIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Stack>
            <Typography variant='h4'>{props.user?.username}</Typography>
          </Stack>
          <Box>
            <IconButton onClick={handleClick}>
              {open
                ? <KeyboardArrowUpRounded sx={{ color: theme.palette.primary.main, fontSize: '2rem' }} />
                : <KeyboardArrowDownRounded sx={{ color: theme.palette.primary.main, fontSize: '2rem' }} />}
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              id='account-menu'
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  py: 1,
                  '& .MuiMenuItem-root': {
                    px: 3,
                  },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {user && accountNavigationList.map((menuItem, index) => {
                const path = menuItem.key
                return (
                  <NavLink
                    key={index}
                    to={`/${user.role.toLowerCase()}/` + path}
                    activeClassName={classes.active}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                    onClick={() => props.setNaviChange && props.setNaviChange(false)}
                  >
                    <MenuItem sx={{ textTransform: 'uppercase' }} selected={path === location.pathname}>
                      <>
                        <ListItemIcon>
                          {menuItem.icon}
                        </ListItemIcon>
                        {t(`coreCommon:${menuItem.label}`)}
                      </>
                    </MenuItem>
                  </NavLink>
                )
              })}
            </Menu>
          </Box>
        </Stack>
      </Box>
    </Stack>
  )
})

Header.displayName = 'Header'

Header.propTypes = {
  user: PropTypes.object,
  logo: PropTypes.string,
  setNaviChange: PropTypes.func,
}

export default Header
